import { createFileRoute } from "@tanstack/react-router";
import {
  ChangeEvent,
  PointerEvent as ReactPointerEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useTranslation from "../../hooks/useTranslation";
import { getCsrfCookie } from "../../lib/utils";

export const Route = createFileRoute("/app/maison_memoire")({
  component: MaisonMemoirePage,
});

type CutoutId = "cup" | "flower" | "notebook";

type CutoutState = {
  id: CutoutId;
  x: number;
  y: number;
  scale: number;
  name?: string;
  bbox?: CutoutBox | null;
  maskPolygon?: CutoutPoint[];
};

type CutoutBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type CutoutPoint = {
  x: number;
  y: number;
};

type CutoutSuggestion =
  | string
  | {
      name?: string;
      bbox?: CutoutBox | null;
      mask_polygon?: CutoutPoint[];
    };

const initialCutouts: CutoutState[] = [
  { id: "cup", x: 68, y: 16, scale: 0.74 },
  { id: "flower", x: 16, y: 46, scale: 0.64 },
  { id: "notebook", x: 82, y: 72, scale: 0.7 },
];

const editorialTextSegments = [
  "Ce matin, je me prepare doucement. Les objets restent proches, presque silencieux, et la journee commence avec une petite promesse. La lumiere ne change pas la scene, elle la rend seulement plus attentive, comme une phrase que l'on relit avant de partir.",
  "Chaque detail garde une trace du moment: la lumiere, la table, le sac, la tasse, le carnet. Rien n'est spectaculaire, mais tout devient personnel. Les choses ordinaires prennent une place calme dans la memoire et donnent au francais un rythme plus intime.",
  "Je regarde cette scene ordinaire et j'y trouve une phrase simple: chaque jour compte, meme quand il commence avec des gestes minuscules. Le texte ne remplace pas l'image; il passe autour d'elle, s'arrete, reprend, et laisse les fragments du reel conduire la page.",
];

type MaisonMemoireGeneratedPayload = {
  headline: string;
  memory: string;
  body_segments: string[];
  cutout_suggestions: CutoutSuggestion[];
  level: string;
};

function resolveCutoutsFromSuggestions(
  suggestions: CutoutSuggestion[],
): CutoutState[] {
  const fallbackBoxes: CutoutBox[] = [
    { x: 18, y: 18, width: 30, height: 34 },
    { x: 52, y: 18, width: 30, height: 34 },
    { x: 32, y: 52, width: 36, height: 34 },
  ];
  return initialCutouts.map((cutout, index) => {
    const suggestion = suggestions[index];
    if (!suggestion || typeof suggestion === "string") {
      return {
        ...cutout,
        name: typeof suggestion === "string" ? suggestion : undefined,
        bbox: fallbackBoxes[index],
        maskPolygon: undefined,
      };
    }
    return {
      ...cutout,
      name: suggestion.name,
      bbox: suggestion.bbox ?? fallbackBoxes[index],
      maskPolygon:
        suggestion.mask_polygon && suggestion.mask_polygon.length >= 6
          ? suggestion.mask_polygon
          : undefined,
    };
  });
}

function MaisonMemoirePage() {
  const { translate } = useTranslation();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [generatedText, setGeneratedText] =
    useState<MaisonMemoireGeneratedPayload | null>(null);
  const [cutouts, setCutouts] = useState<CutoutState[]>(initialCutouts);
  const [dragging, setDragging] = useState<{
    id: CutoutId;
    pointerId: number;
    originX: number;
    originY: number;
    hasMoved: boolean;
  } | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const flowRef = useRef<HTMLElement | null>(null);
  const pendingDragRef = useRef<{
    id: CutoutId;
    clientX: number;
    clientY: number;
  } | null>(null);
  const dragFrameRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  useEffect(() => {
    if (!dragging) {
      return;
    }
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "grabbing";
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [dragging]);

  useEffect(() => {
    return () => {
      if (dragFrameRef.current !== null) {
        window.cancelAnimationFrame(dragFrameRef.current);
      }
    };
  }, []);

  const activeMemoryText =
    generatedText?.memory || translate("maison_memoire_french_b1");
  const activeBodySegments =
    generatedText?.body_segments?.length === 3
      ? generatedText.body_segments
      : editorialTextSegments;
  const activeHeadline =
    generatedText?.headline || translate("maison_memoire_editorial_word");
  const orderedCutouts = useMemo(
    () => [...cutouts].sort((left, right) => left.y - right.y),
    [cutouts],
  );

  const generateFromImage = async (file: File) => {
    const formData = new FormData();
    formData.append("image", file);
    formData.append("level", "B1");
    const response = await fetch("/api/reeprise/maison-memoire/generate/", {
      method: "POST",
      headers: {
        "X-CSRFTOKEN": getCsrfCookie(),
      },
      body: formData,
    });
    if (!response.ok) {
      throw new Error("Could not generate Maison Mémoire text");
    }
    return (await response.json()) as MaisonMemoireGeneratedPayload;
  };

  const handleUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }
    setImageUrl(URL.createObjectURL(file));
    setGeneratedText(null);
    setCutouts(initialCutouts);
    event.target.value = "";
    void generateFromImage(file)
      .then((payload) => {
        setGeneratedText(payload);
        setCutouts(resolveCutoutsFromSuggestions(payload.cutout_suggestions));
      })
      .catch(() => {
        setGeneratedText({
          headline: translate("maison_memoire_editorial_word"),
          memory: translate("maison_memoire_french_b1"),
          body_segments: editorialTextSegments,
          cutout_suggestions: [],
          level: "B1",
        });
        setCutouts(resolveCutoutsFromSuggestions([]));
      });
  };

  const commitCutoutPosition = (id: CutoutId, clientX: number, clientY: number) => {
    const bounds =
      flowRef.current?.getBoundingClientRect() ??
      stageRef.current?.getBoundingClientRect();
    if (!bounds) {
      return;
    }
    const x = ((clientX - bounds.left) / bounds.width) * 100;
    const y = ((clientY - bounds.top) / bounds.height) * 100;
    setCutouts((items) =>
      items.map((item) =>
        item.id === id
          ? {
              ...item,
              x: Math.min(96, Math.max(4, x)),
              y: Math.min(98, Math.max(2, y)),
            }
          : item,
      ),
    );
  };

  const updateCutoutPosition = (
    id: CutoutId,
    event: ReactPointerEvent<HTMLButtonElement>,
  ) => {
    pendingDragRef.current = {
      id,
      clientX: event.clientX,
      clientY: event.clientY,
    };
    if (dragFrameRef.current !== null) {
      return;
    }
    dragFrameRef.current = window.requestAnimationFrame(() => {
      dragFrameRef.current = null;
      const pendingDrag = pendingDragRef.current;
      if (!pendingDrag) {
        return;
      }
      commitCutoutPosition(
        pendingDrag.id,
        pendingDrag.clientX,
        pendingDrag.clientY,
      );
    });
  };

  return (
    <main
      ref={stageRef}
      className="min-h-screen overflow-hidden bg-[#f4f0e8] text-neutral-950"
    >
      {!imageUrl && (
        <div className="flex min-h-screen items-center justify-center px-5">
          <label className="cursor-pointer border border-neutral-950 px-5 py-3 text-base font-black uppercase tracking-normal transition hover:bg-[#d7f42f]">
            {translate("maison_memoire_upload")}
            <input
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="sr-only"
            />
          </label>
        </div>
      )}

      {imageUrl && (
        <article
          ref={flowRef}
          className={`relative min-h-screen px-5 py-8 sm:px-10 sm:py-12 lg:px-16 ${
            dragging ? "cursor-grabbing select-none" : ""
          }`}
        >
          <div className="mx-auto max-w-6xl">
            <h1 className="mb-6 max-w-[34rem] text-sm font-black uppercase leading-none tracking-normal text-neutral-950">
              {activeHeadline || translate("maison_memoire_editorial_word")}
            </h1>

            <div className="columns-1 gap-8 text-justify text-[0.82rem] leading-[1.45] text-neutral-950 sm:columns-3 lg:columns-5">
              <p className="mb-4 break-inside-avoid font-semibold uppercase">
                {activeMemoryText}
              </p>
              {activeBodySegments
                .flatMap((segment) => [segment, segment])
                .map((segment, segmentIndex) => {
                  const segmentCutouts = orderedCutouts.filter((cutout) => {
                    const bucket = Math.min(
                      activeBodySegments.length * 2 - 1,
                      Math.floor((cutout.y / 100) * activeBodySegments.length * 2),
                    );
                    return bucket === segmentIndex;
                  });
                  return (
                    <p key={`${segment}-${segmentIndex}`} className="mb-4">
                      {segmentCutouts.map((cutout) => (
                        <FlowCutout
                          key={cutout.id}
                          cutout={cutout}
                          dragging={dragging}
                          imageUrl={imageUrl}
                          translate={translate}
                          onDragStart={(event) => {
                            event.currentTarget.setPointerCapture(event.pointerId);
                            setDragging({
                              id: cutout.id,
                              pointerId: event.pointerId,
                              originX: event.clientX,
                              originY: event.clientY,
                              hasMoved: false,
                            });
                          }}
                          onDragMove={(event) => {
                            if (
                              dragging?.id === cutout.id &&
                              dragging.pointerId === event.pointerId
                            ) {
                              const movedDistance = Math.hypot(
                                event.clientX - dragging.originX,
                                event.clientY - dragging.originY,
                              );
                              if (!dragging.hasMoved && movedDistance < 4) {
                                return;
                              }
                              if (!dragging.hasMoved) {
                                setDragging({ ...dragging, hasMoved: true });
                              }
                              updateCutoutPosition(cutout.id, event);
                            }
                          }}
                          onDragEnd={(event) => {
                            if (dragging?.pointerId === event.pointerId) {
                              const shouldCommit = dragging.hasMoved;
                              setDragging(null);
                              if (shouldCommit) {
                                commitCutoutPosition(
                                  cutout.id,
                                  event.clientX,
                                  event.clientY,
                                );
                              }
                            }
                          }}
                        />
                      ))}
                      {segment}
                    </p>
                  );
                })}
            </div>
          </div>
        </article>
      )}
    </main>
  );
}

function FlowCutout({
  cutout,
  dragging,
  imageUrl,
  translate,
  onDragStart,
  onDragMove,
  onDragEnd,
}: {
  cutout: CutoutState;
  dragging: {
    id: CutoutId;
    pointerId: number;
    originX: number;
    originY: number;
    hasMoved: boolean;
  } | null;
  imageUrl: string | null;
  translate: (translationKey: string) => string;
  onDragStart: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  onDragMove: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  onDragEnd: (event: ReactPointerEvent<HTMLButtonElement>) => void;
}) {
  const isLeft = cutout.x < 50;
  const size = cutout.id === "notebook" ? 132 : 112;
  const scaledSize = Math.round(size * cutout.scale);
  const shapeById: Record<CutoutId, string> = {
    cup: "polygon(8% 28%, 18% 10%, 54% 0%, 88% 16%, 96% 54%, 78% 90%, 34% 100%, 4% 76%)",
    flower:
      "polygon(42% 0%, 66% 8%, 91% 28%, 100% 62%, 75% 92%, 42% 100%, 11% 82%, 0% 44%, 15% 16%)",
    notebook:
      "polygon(2% 26%, 30% 4%, 86% 12%, 100% 42%, 88% 91%, 31% 100%, 6% 72%)",
  };
  const shape = cutout.maskPolygon?.length
    ? `polygon(${cutout.maskPolygon
        .map((point) => `${point.x}% ${point.y}%`)
        .join(", ")})`
    : shapeById[cutout.id];
  const sideOffset = isLeft
    ? Math.max(0, Math.round((cutout.x / 50) * 160 - 36))
    : Math.max(0, Math.round(((100 - cutout.x) / 50) * 160 - 36));
  return (
    <button
      type="button"
      aria-label={translate(`maison_memoire_cutout_${cutout.id}`)}
      data-testid={`maison-cutout-${cutout.id}`}
      onPointerDown={onDragStart}
      onPointerMove={onDragMove}
      onPointerUp={onDragEnd}
      onPointerCancel={onDragEnd}
      onLostPointerCapture={onDragEnd}
      className={`touch-none align-top cursor-grab active:cursor-grabbing focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-neutral-950 ${
        isLeft ? "float-left mr-5" : "float-right ml-5"
      }`}
      style={{
        width: `min(${scaledSize}px, 34vw)`,
        height: `min(${Math.round(scaledSize * 0.82)}px, 28vw)`,
        clipPath: shape,
        shapeOutside: shape,
        shapeMargin: dragging?.id === cutout.id ? "18px" : "10px",
        marginLeft: isLeft ? `${sideOffset}px` : "20px",
        marginRight: isLeft ? "20px" : `${sideOffset}px`,
        marginTop: `${Math.max(0, Math.round((cutout.y % 28) * 0.6))}px`,
        marginBottom: "10px",
      }}
    >
      <CutoutVisual cutout={cutout} imageUrl={imageUrl} shape={shape} />
    </button>
  );
}

function CutoutVisual({
  cutout,
  imageUrl,
  shape,
}: {
  cutout: CutoutState;
  imageUrl: string | null;
  shape: string;
}) {
  if (imageUrl) {
    const objectPositionById: Record<CutoutId, string> = {
      cup: "36% 36%",
      flower: "58% 34%",
      notebook: "48% 70%",
    };
    const bbox = cutout.bbox;
    const imageStyle = bbox
      ? {
          left: `${-(bbox.x / bbox.width) * 100}%`,
          top: `${-(bbox.y / bbox.height) * 100}%`,
          width: `${(100 / bbox.width) * 100}%`,
          height: `${(100 / bbox.height) * 100}%`,
          maxWidth: "none",
        }
      : {
          objectPosition: objectPositionById[cutout.id],
        };
    return (
      <div className="relative h-full w-full overflow-hidden bg-neutral-200">
        <img
          src={imageUrl}
          alt=""
          aria-hidden="true"
          className={bbox ? "absolute object-cover" : "h-full w-full scale-125 object-cover"}
          style={imageStyle}
        />
      </div>
    );
  }
  if (cutout.id === "cup") {
    return (
      <div
        className="relative h-full w-full bg-[#e8e1d2]"
        style={{ clipPath: shape }}
      >
        <div className="absolute left-[18%] top-[22%] h-[56%] w-[52%] border-4 border-neutral-950" />
        <div className="absolute right-[14%] top-[38%] h-[26%] w-[22%] border-4 border-neutral-950" />
        <div className="absolute left-[31%] top-[4%] h-[24%] w-[3%] bg-neutral-950/70" />
        <div className="absolute left-[48%] top-[0%] h-[30%] w-[3%] bg-neutral-950/45" />
      </div>
    );
  }
  if (cutout.id === "flower") {
    return (
      <div
        className="relative h-full w-full bg-[#f4c7cf]"
        style={{ clipPath: shape }}
      >
        <div className="absolute left-[49%] top-[42%] h-[54%] w-[4%] rotate-12 bg-emerald-900" />
        <div className="absolute left-[18%] top-[12%] h-[44%] w-[42%] border-2 border-rose-900 bg-rose-200" />
        <div className="absolute left-[48%] top-[24%] h-[34%] w-[38%] border-2 border-rose-900 bg-rose-100" />
        <div className="absolute left-[42%] top-[34%] h-[16%] w-[16%] bg-[#d7f42f]" />
      </div>
    );
  }
  return (
    <div
      className="relative h-full w-full bg-[#e5dac4]"
      style={{ clipPath: shape }}
    >
      <div className="absolute left-[15%] top-[34%] h-[10%] w-[55%] rotate-[-7deg] bg-neutral-950/50" />
      <div className="absolute left-[18%] top-[54%] h-[8%] w-[62%] rotate-[-7deg] bg-neutral-950/30" />
    </div>
  );
}
